import {
    FlexRow,
    FlexRowBetween,
    ImageContainer,
    PageHeader,
    AppEditButton,
    AppRemoveButtonPopup,
    NumericStepper,
} from 'components'
import React, { useState, useEffect } from 'react'
import { Header, Modal, Popup, Form, Checkbox } from 'semantic-ui-react'
import styled from 'styled-components'
import { PencilSimple, Plus, X, CaretRight, CaretDown, Check, Tag, ListDashes } from 'phosphor-react'
import {
    TemplateResponseType,
    isAutoNumberResponseItem,
    AutoNumberType,
    parseAutoNumberConfig,
    AUTO_NUMBER_DEFAULT_START,
    normalizeNumericAutoNumberStartFrom,
    isValidNumericAutoNumberStartFrom,
    normalizeAlphaNumericAutoNumberStartFrom,
    parseAlphaNumericAutoNumberStartFrom,
    formatAlphaNumericStartFromDisplay,
    isFormulaResponseItem,
    isFormulaQuestionCreationEnabled,
} from 'utility'
import { useAuth } from 'context'
import { AppButton, AppPrimaryButton } from 'elements'
import { DesktopMode, MobileMode, useTabletAndBelowMediaQuery } from 'layout'

/* ─── Outer list styled-components (unchanged) ─── */

const ResponseContainer = styled.div`
    div { cursor: pointer; }
    .wrap { display: flex; flex-wrap: wrap; row-gap: 10px; }
`

/* Align secondary lines (parent/child summary, nested summary) under response text, past the icon */
const ResponseSubLine = styled.div`
    margin-top: 4px;
    padding-left: ${({ $corner }) => ($corner ? 0 : '30px')};
    width: 100%;
    box-sizing: border-box;
    text-align: ${({ $corner }) => ($corner ? 'right' : 'left')};
`

const SingleTypeStack = styled.div`
    display: flex;
    flex-direction: column;
    align-items: ${({ $corner }) => ($corner ? 'flex-end' : 'stretch')};
`

const MultiOptions = styled.span`
    padding: 0.6rem; border-radius: 10px; margin-right: 0.5rem;
`

const MultiChoiceContainer = styled(FlexRowBetween)`
    display: flex; align-items: center;
    svg { cursor: pointer; }
    &:hover { background: #f2f2f2; }
`
const ResponseModal = styled.div`
    margin-top: 20px; border-top: solid 1px grey;
`

const MultiChoiceWrapper = styled.div`
    display: flex; flex-wrap: wrap; justify-content: space-between; gap: 10px;
    .single-option {
        width: ${({ isMobile }) => (isMobile ? '100%' : '48%')};
        padding: 10px; background: #0000000a; border-radius: 15px;
        &:hover { background: #00000021; }
        .tag-design { background: #0000000a; padding: 10px; border-radius: 15px; }
    }
`

/* ─── CustomModal styled wrappers ─── */

const ModalBody = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    max-height: calc(78vh - 130px);
    overflow-y: auto;
    padding: 16px 20px;
`

const SectionCard = styled.div`
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    background: #fff;
`

const SectionCardHeader = styled.div`
    background: #f8fafc;
    padding: 8px 14px;
    border-bottom: 1px solid #e2e8f0;
    display: flex;
    align-items: center;
    justify-content: space-between;
`

const SectionCardTitle = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 11px;
    font-weight: 700;
    color: #374151;
    text-transform: uppercase;
    letter-spacing: 0.05em;
`

const StepDot = styled.span`
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: #0f1f3d;
    color: #fff;
    font-size: 9px;
    font-weight: 700;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
`

const SectionCardBody = styled.div`
    padding: 14px 16px;
    overflow-y: auto
    flex: 1;
`

const ParentRow = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 10px;
    background: #fff;
    cursor: pointer;
    min-height: 44px;
    &:hover { background: #f9fafb; }
`

const ParentCard = styled.div`
    border: 1.5px solid ${({ hasChildren }) => (hasChildren ? '#bfdbfe' : '#e5e7eb')};
    border-left: ${({ allowManual }) => (allowManual ? '3px solid #7c3aed' : undefined)};
    border-radius: 10px;
    overflow: hidden;
    margin-bottom: 8px;
    transition: border-color 0.15s;
    &:hover { border-color: #d1d5db; }
`

const ChildrenPanel = styled.div`
    display: ${({ open }) => (open ? 'block' : 'none')};
    border-top: 1px solid #bfdbfe;
    background: #f7fbff;
    padding: 10px 12px 10px 34px;
`

const ChildRow = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 5px 8px;
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 7px;
    margin-bottom: 5px;
    &:hover { border-color: #d1d5db; }
`

const ChildLabel = styled.div`
    flex: 1;
    font-size: 12.5px;
    color: #111827;
    font-weight: 500;
`

const ScorePill = styled.span`
    font-size: 11px;
    font-weight: 700;
    color: #d97706;
    background: #fffbeb;
    border: 1px solid #fde68a;
    border-radius: 4px;
    padding: 1px 6px;
    font-family: monospace;
`

const AddChildRow = styled.div`
    display: flex;
    gap: 7px;
    align-items: center;
    padding: 7px 9px;
    background: #eef4ff;
    border: 1.5px dashed #bfdbfe;
    border-radius: 7px;
    flex-wrap: wrap;
    margin-top: 8px;
`

const BadgeRow = styled.div`
    display: flex;
    gap: 5px;
    flex-wrap: wrap;
    align-items: center;
`

const Badge = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 3px;
    font-size: 10px;
    font-weight: 600;
    padding: 2px 7px;
    border-radius: 5px;
    white-space: nowrap;
    background: ${({ bg }) => bg || '#eff6ff'};
    color: ${({ color }) => color || '#2563eb'};
    border: 1px solid ${({ border }) => border || '#bfdbfe'};
`

const ChipRow = styled.div`
    display: flex;
    gap: 7px;
    flex-wrap: wrap;
`

const Chip = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 5px;
    font-size: 12px;
    font-weight: 600;
    padding: 4px 10px;
    border-radius: 6px;
    background: ${({ bg }) => bg || '#f0f9ff'};
    color: ${({ color }) => color || '#0369a1'};
    border: 1px solid ${({ border }) => border || '#bae6fd'};
`

const ChevBtn = styled.button`
    width: 20px;
    height: 20px;
    border-radius: 5px;
    border: 1px solid ${({ open }) => (open ? '#bfdbfe' : '#e5e7eb')};
    background: ${({ open }) => (open ? '#eff6ff' : '#fff')};
    color: ${({ open }) => (open ? '#2563eb' : '#9ca3af')};
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    padding: 0;
    transition: all 0.15s;
`

const ChildLabelRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 10px;
    padding: 7px 10px;
    background: #fff;
    border: 1.5px solid #e5e7eb;
    border-radius: 8px;
    &:focus-within { border-color: #2563eb; }
`

const ChildLabelText = styled.span`
    font-size: 11.5px;
    font-weight: 700;
    color: #6b7280;
    white-space: nowrap;
`

const ChildLabelInput = styled.input`
    flex: 1;
    border: none !important;
    font-size: 12.5px;
    color: #111827;
    padding: 0 6px !important;
    outline: none;
    box-shadow: none !important;
    background: transparent;
    height: 24px !important;
    &::placeholder { color: #9ca3af; }
`

const EmptyState = styled.div`
    text-align: center;
    padding: 28px 16px;
    color: #9ca3af;
`

const EditOverlay = styled.div`
    position: fixed;
    inset: 0;
    background: rgba(10, 20, 50, 0.55);
    backdrop-filter: blur(3px);
    z-index: 2000;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;
`

const EditCard = styled.div`
    background: #fff;
    border-radius: 14px;
    box-shadow: 0 20px 48px rgba(0, 0, 0, 0.16), 0 8px 16px rgba(0, 0, 0, 0.08);
    width: 100%;
    max-width: 460px;
    overflow: hidden;
`

const AUTO_NUMBER_START_PLACEHOLDER = AUTO_NUMBER_DEFAULT_START

const AutoNumberOptionRow = styled.div`
    display: flex;
    align-items: flex-end;
    flex-wrap: nowrap;
    gap: 12px;
    margin-bottom: 14px;
    width: 100%;

    .auto-number-option-check {
        flex: 0 0 auto;
        align-self: flex-end;

        .ui.checkbox {
            margin: 0;
            width: auto;
            min-width: 0;
            display: inline-flex;
        }

        .ui.checkbox label {
            white-space: nowrap;
        }
    }

    .auto-number-inline-fields {
        display: flex;
        align-items: flex-end;
        flex-wrap: nowrap;
        gap: 10px;
        flex: 1 1 auto;
        min-width: 0;
    }

    .auto-number-inline-field {
        display: flex;
        flex-direction: column;
        align-items: stretch;
        gap: 4px;
        flex: 1 1 0;
        min-width: 0;
        max-width: 180px;

        label {
            margin: 0;
            white-space: nowrap;
            font-weight: 600;
            color: #374151;
            font-size: 0.9rem;
        }

        .ui.input {
            width: 100%;
            min-width: 0;
        }
    }
`

const buildAutoNumberSummaryText = config => {
    const cfg = config || {}
    const type = Number(cfg.AutoNumberType)
    if (type === AutoNumberType.Numeric) {
        const start = String(cfg.AutoNumberStartFrom || '').trim()
        return start ? `Numeric · ${start}` : 'Numeric'
    }
    if (type === AutoNumberType.AlphaNumeric) {
        const prefix = String(cfg.AutoNumberPreFix || '').trim()
        const start = String(cfg.AutoNumberStartFrom || '').trim()
        if (prefix && start) return `Alpha numeric · ${prefix}${start}`
        if (prefix) return `Alpha numeric · ${prefix}`
        if (start) return `Alpha numeric · ${start}`
        return 'Alpha numeric'
    }
    return ''
}

/* ─── Auto Number config ─── */

export const AutoNumberConfigModal = ({
    open,
    onClose,
    existingData = null,
}) => {
    const [autoNumberType, setAutoNumberType] = useState(0)
    const [prefix, setPrefix] = useState('')
    const [startFrom, setStartFrom] = useState('')
    const [selectionError, setSelectionError] = useState('')

    useEffect(() => {
        if (!open) return
        setSelectionError('')
        const parsed = parseAutoNumberConfig(existingData)
        setAutoNumberType(parsed.AutoNumberType)
        setPrefix(parsed.AutoNumberPreFix)
        const start = String(parsed.AutoNumberStartFrom || '').trim()
        if (parsed.AutoNumberType === AutoNumberType.Numeric) {
            setStartFrom(
                normalizeNumericAutoNumberStartFrom(start) ||
                    AUTO_NUMBER_DEFAULT_START,
            )
        } else if (parsed.AutoNumberType === AutoNumberType.AlphaNumeric) {
            setStartFrom(
                formatAlphaNumericStartFromDisplay(
                    parsed.AutoNumberStartFrom,
                    parsed.AutoNumberPadWidth,
                ) ||
                    start ||
                    AUTO_NUMBER_DEFAULT_START,
            )
        } else {
            setStartFrom(start || AUTO_NUMBER_DEFAULT_START)
        }
    }, [open, existingData])

    const handleNumericChange = (_, { checked }) => {
        if (checked) {
            setAutoNumberType(AutoNumberType.Numeric)
            setPrefix('')
            setStartFrom(AUTO_NUMBER_DEFAULT_START)
        } else {
            setAutoNumberType(0)
            setPrefix('')
            setStartFrom('')
        }
        setSelectionError('')
    }

    const handleAlphaNumericChange = (_, { checked }) => {
        if (checked) {
            setAutoNumberType(AutoNumberType.AlphaNumeric)
            setPrefix('')
            setStartFrom(AUTO_NUMBER_DEFAULT_START)
        } else {
            setAutoNumberType(0)
            setPrefix('')
            setStartFrom('')
        }
        setSelectionError('')
    }

    const handleSubmit = () => {
        const type = Number(autoNumberType)
        if (!type) {
            setSelectionError('Please select Numeric or Alpha numeric.')
            return
        }
        const startVal = String(startFrom ?? '').trim()
        if (!startVal) {
            setSelectionError('Start from is required.')
            return
        }
        if (
            type === AutoNumberType.Numeric &&
            !isValidNumericAutoNumberStartFrom(startVal)
        ) {
            setSelectionError(
                'Start from must be 1 or greater and cannot begin with 0.',
            )
            return
        }
        if (type === AutoNumberType.AlphaNumeric) {
            const prefixVal = String(prefix ?? '').trim()
            if (!prefixVal) {
                setSelectionError('PreFix is required.')
                return
            }
            const alphaParsed = parseAlphaNumericAutoNumberStartFrom(startVal)
            if (!alphaParsed.valid) {
                setSelectionError(
                    'Start from must be 1 or greater and cannot be only zeros.',
                )
                return
            }
            onClose({
                AutoNumberType: type,
                AutoNumberPreFix: prefixVal,
                AutoNumberStartFrom: alphaParsed.startFrom,
                AutoNumberPadWidth: alphaParsed.padWidth,
            })
            return
        }
        onClose({
            AutoNumberType: AutoNumberType.Numeric,
            AutoNumberPreFix: '',
            AutoNumberStartFrom: normalizeNumericAutoNumberStartFrom(startVal),
        })
    }

    const isNumeric = autoNumberType === AutoNumberType.Numeric
    const isAlphaNumeric = autoNumberType === AutoNumberType.AlphaNumeric

    return (
        <Modal
            open={open}
            onClose={() => onClose(null)}
            size="small"
            style={{ width: 520, maxWidth: '95vw' }}
        >
            <Modal.Header>
                <FlexRowBetween style={{ alignItems: 'center' }}>
                    <span style={{ fontWeight: 700 }}>Auto Number</span>
                    <AppButton
                        type="button"
                        onClick={() => onClose(null)}
                        style={{ padding: '4px 8px' }}
                    >
                        <X size={18} weight="bold" />
                    </AppButton>
                </FlexRowBetween>
            </Modal.Header>
            <Modal.Content>
                <Form error={!!selectionError}>
                    <AutoNumberOptionRow>
                        <div className="auto-number-option-check">
                            <Checkbox
                                label="Numeric"
                                checked={isNumeric}
                                onChange={handleNumericChange}
                            />
                        </div>
                        {isNumeric && (
                            <div className="auto-number-inline-fields">
                                <div className="auto-number-inline-field">
                                    <label>Start from</label>
                                    <Form.Input
                                        placeholder={
                                            AUTO_NUMBER_START_PLACEHOLDER
                                        }
                                        value={startFrom}
                                        inputMode="numeric"
                                        onChange={e => {
                                            setStartFrom(
                                                normalizeNumericAutoNumberStartFrom(
                                                    e.target.value,
                                                ),
                                            )
                                            setSelectionError('')
                                        }}
                                    />
                                </div>
                            </div>
                        )}
                    </AutoNumberOptionRow>
                    <AutoNumberOptionRow>
                        <div className="auto-number-option-check">
                            <Checkbox
                                label="Alpha numeric"
                                checked={isAlphaNumeric}
                                onChange={handleAlphaNumericChange}
                            />
                        </div>
                        {isAlphaNumeric && (
                            <div className="auto-number-inline-fields">
                                <div className="auto-number-inline-field">
                                    <label>PreFix</label>
                                    <Form.Input
                                        placeholder="AB"
                                        value={prefix}
                                        onChange={e => {
                                            setPrefix(e.target.value)
                                            setSelectionError('')
                                        }}
                                    />
                                </div>
                                <div className="auto-number-inline-field">
                                    <label>Start from</label>
                                    <Form.Input
                                        placeholder={
                                            AUTO_NUMBER_START_PLACEHOLDER
                                        }
                                        value={startFrom}
                                        inputMode="numeric"
                                        onChange={e => {
                                            setStartFrom(
                                                normalizeAlphaNumericAutoNumberStartFrom(
                                                    e.target.value,
                                                ),
                                            )
                                            setSelectionError('')
                                        }}
                                    />
                                </div>
                            </div>
                        )}
                    </AutoNumberOptionRow>
                    {selectionError ? (
                        <div
                            style={{
                                color: '#c62828',
                                fontSize: '0.9rem',
                                marginTop: 8,
                            }}
                        >
                            {selectionError}
                        </div>
                    ) : null}
                </Form>
            </Modal.Content>
            <Modal.Actions>
                <AppButton type="button" onClick={() => onClose(null)}>
                    Cancel
                </AppButton>
                <AppPrimaryButton type="button" onClick={handleSubmit}>
                    Save
                </AppPrimaryButton>
            </Modal.Actions>
        </Modal>
    )
}

/* ─── Helpers ─── */

const stripHtmlTags = html => {
    if (!html) return ''
    const t = document.createElement('div')
    t.innerHTML = html
    return t.textContent || t.innerText || ''
}

/* ─── ResponseTag ─── */

export const ResponseTag = ({
    item,
    type,
    question,
    onParentChildDataReceived,
    onAutoNumberDataReceived,
    onFormulaOpen,
    formulaSummary = '',
    existingData,
    existingAutoNumberData = null,
    nestedSummary = '',
    nestedDetail = '',
    /** Right-align main type + sublines (e.g. corner layout like Parent Child) */
    cornerLayout = false,
}) => {
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [isAutoNumberModalOpen, setIsAutoNumberModalOpen] = useState(false)

    const isAutoNumber = isAutoNumberResponseItem(item)
    const isFormula =
        type === TemplateResponseType.Single && isFormulaResponseItem(item)

    const handleClick = () => {
        if (item.NestedOption) setIsModalOpen(true)
        else if (isAutoNumber) setIsAutoNumberModalOpen(true)
        else if (isFormula && onFormulaOpen) onFormulaOpen()
    }

    const handleCustomModalClose = data => {
        setIsModalOpen(false)
        if (data && onParentChildDataReceived) onParentChildDataReceived(data)
    }

    const handleAutoNumberModalClose = data => {
        setIsAutoNumberModalOpen(false)
        if (data && onAutoNumberDataReceived) onAutoNumberDataReceived(data)
    }

    const isParentChild = type === TemplateResponseType.Single && (item?.DisplayText || '').trim().toLowerCase() === 'parent child'

    const buildParentChildSummary = data => {
        if (!Array.isArray(data) || data.length === 0) return ''
        const firstParent = data[0]
        if (!firstParent) return ''
        const children = Array.isArray(firstParent.Children) && firstParent.Children.length ? firstParent.Children.map(c => c.ChildName).join(', ') : ''
        const base = children ? `${firstParent.ParentName}: ${children}` : firstParent.ParentName
        const maxChars = 30
        if (base.length <= maxChars) return base
        const visibleLen = Math.max(0, maxChars - 7)
        return `${base.slice(0, visibleLen)}.......`
    }

    const buildParentChildDetail = data => {
        if (!Array.isArray(data) || data.length === 0) return 'No parent-child options configured.'
        return data.map(parent => {
            const parentScore = parent.IsScore && typeof parent.Score === 'number' ? ` (Score: ${parent.Score})` : ''
            const children = Array.isArray(parent.Children) && parent.Children.length
                ? parent.Children.map(child => {
                    let rawScore = null
                    if (child.IsChildScore && typeof child.ChildScore === 'number') rawScore = child.ChildScore
                    else if (child.IsScore && typeof child.Score === 'number') rawScore = child.Score
                    const childScore = rawScore !== null ? ` (Score: ${rawScore})` : ''
                    return `${child.ChildName}${childScore}`
                }).join(', ')
                : 'None'
            const flags = [
                parent.AllowManualScore ? 'Manual score' : null,
                parent.IsMultipleAllow ? 'Multiple selection' : null,
                parent.IsOtherOptionAvailable ? 'Other response' : null,
            ].filter(Boolean).join(', ')
            const flagLine = flags ? `\nOptions: ${flags}` : ''
            return `Parent: ${parent.ParentName}${parentScore}${flagLine}\nChildren: ${children}`
        }).join('\n\n')
    }

    const parentChildSummary = isParentChild ? buildParentChildSummary(existingData) : ''
    const parentChildDetail = isParentChild ? buildParentChildDetail(existingData) : ''

    const autoNumberSummary =
        isAutoNumber && existingAutoNumberData
            ? buildAutoNumberSummaryText(existingAutoNumberData)
            : ''

    const showNestedHover =
        type === TemplateResponseType.Single &&
        !!nestedSummary &&
        !!nestedDetail

    return (
        <ResponseContainer className="">
            {type === TemplateResponseType.Multiple ? (
                <div className="wrap">
                    {(item?.Items || []).map(option => (
                        <MultiOptions style={{ background: option.BackColorCode }} key={option.Id}>
                            {' '}{option.DisplayText}
                        </MultiOptions>
                    ))}
                </div>
            ) : (
                <SingleTypeStack $corner={cornerLayout}>
                    <FlexRow
                        className="align-items-center flex-nowrap"
                        style={{
                            ...(cornerLayout
                                ? { alignSelf: 'flex-end' }
                                : {}),
                            ...(item.NestedOption || isAutoNumber || isFormula
                                ? { cursor: 'pointer' }
                                : {}),
                        }}
                        onClick={handleClick}
                    >
                        <ImageContainer
                            src={`/images/svg/${(
                                item.IconDisplayText || item.DisplayText
                            )
                                .trim()}.svg`}
                            className="mr-2"
                            width={22}
                            height={22}
                        />
                        {item.DisplayText}
                    </FlexRow>
                    {isParentChild && parentChildSummary && (
                        <ResponseSubLine $corner={cornerLayout}>
                            <Popup
                                hoverable
                                position={
                                    cornerLayout ? 'bottom right' : 'bottom center'
                                }
                                content={
                                    <pre style={{ whiteSpace: 'pre-wrap', margin: 0, fontFamily: 'inherit' }}>
                                        {parentChildDetail}
                                    </pre>
                                }
                                trigger={
                                    <span
                                        style={{ fontSize: '1rem', color: '#555', cursor: 'pointer', textDecoration: 'underline dotted' }}
                                        onClick={e => e.stopPropagation()}
                                    >
                                        {parentChildSummary}
                                    </span>
                                }
                            />
                        </ResponseSubLine>
                    )}
                    {isFormula && cornerLayout && formulaSummary && (
                        <ResponseSubLine $corner={cornerLayout}>
                            <span
                                style={{
                                    fontSize: '1rem',
                                    color: '#555',
                                    cursor: 'pointer',
                                    textDecoration: 'underline dotted',
                                }}
                                onClick={e => {
                                    e.stopPropagation()
                                    if (onFormulaOpen) onFormulaOpen()
                                }}
                            >
                                {formulaSummary}
                            </span>
                        </ResponseSubLine>
                    )}
                    {isAutoNumber && cornerLayout && autoNumberSummary && (
                        <ResponseSubLine $corner={cornerLayout}>
                            <span
                                style={{
                                    fontSize: '1rem',
                                    color: '#555',
                                    cursor: 'pointer',
                                    textDecoration: 'underline dotted',
                                }}
                                onClick={e => {
                                    e.stopPropagation()
                                    setIsAutoNumberModalOpen(true)
                                }}
                            >
                                {autoNumberSummary}
                            </span>
                        </ResponseSubLine>
                    )}
                    {showNestedHover && (
                        <ResponseSubLine $corner={cornerLayout}>
                            <Popup
                                hoverable
                                position={
                                    cornerLayout ? 'bottom right' : 'bottom center'
                                }
                                content={
                                    <pre style={{ whiteSpace: 'pre-wrap', margin: 0, fontFamily: 'inherit' }}>
                                        {nestedDetail}
                                    </pre>
                                }
                                trigger={
                                    <span
                                        style={{ fontSize: '1rem', color: '#555' }}
                                        onClick={e => e.stopPropagation()}
                                    >
                                        <span
                                            style={{
                                                color: '#14532d',
                                                fontWeight: 700,
                                                marginRight: 6,
                                            }}
                                        >
                                            Nested
                                        </span>
                                        <span
                                            style={{
                                                cursor: 'pointer',
                                                textDecoration: 'underline dotted',
                                            }}
                                        >
                                            {nestedSummary}
                                        </span>
                                    </span>
                                }
                            />
                        </ResponseSubLine>
                    )}
                </SingleTypeStack>
            )}
            {item.NestedOption && (
                <CustomModal
                    open={isModalOpen}
                    onClose={handleCustomModalClose}
                    question={question}
                    existingData={existingData}
                />
            )}
            {isAutoNumber && (
                <AutoNumberConfigModal
                    open={isAutoNumberModalOpen}
                    onClose={handleAutoNumberModalClose}
                    existingData={existingAutoNumberData}
                />
            )}
        </ResponseContainer>
    )
}

/* ─── CustomModal ─── */

export const CustomModal = ({ open, onClose, question, existingData }) => {
    /* ── state ── */
    const [parents, setParents] = useState([])
    const [exp, setExp] = useState(new Set())
    const [errIds, setErrIds] = useState(new Set())

    // new-parent form
    const [npName, setNpName] = useState('')
    const [npScore, setNpScore] = useState(0)
    const [npOther, setNpOther] = useState(false)
    const [npManual, setNpManual] = useState(false)
    const [npMultiple, setNpMultiple] = useState(false)
    const [npMaxScore, setNpMaxScore] = useState(10)

    // per-parent add-child form: { [pid]: { name, score } }
    const [ncData, setNcData] = useState({})

    // per-parent child-label inputs
    const [clblInputs, setClblInputs] = useState({})

    // edit overlay
    const [editCtx, setEditCtx] = useState(null)
    const [oName, setOName] = useState('')
    const [oScore, setOScore] = useState(0)
    const [oOther, setOOther] = useState(false)
    const [oManual, setOManual] = useState(false)
    const [oMultiple, setOMultiple] = useState(false)
    const [oMaxScore, setOMaxScore] = useState(10)

    /* ── error flash ── */
    const showError = id => {
        setErrIds(p => new Set(p).add(id))
        setTimeout(() => {
            setErrIds(p => {
                const n = new Set(p)
                n.delete(id)
                return n
            })
        }, 800)
    }

    /* ── populate from existingData ── */
    useEffect(() => {
        if (!existingData) return
        const newParents = existingData.map((p, i) => {
            const hasManual = !!p.AllowManualScore
            let pScore = 0
            if (p.IsScore && typeof p.Score === 'number') pScore = p.Score
            return {
                id: p.ParentId || `pid-${Date.now()}-${i}`,
                ParentId: p.ParentId,
                name: p.ParentName || '',
                score: hasManual ? 0 : pScore,
                allowOther: !!p.IsOtherOptionAvailable,
                allowManual: hasManual,
                isMultipleAllow: !!p.IsMultipleAllow,
                maxScore: typeof p.MaxManualScore === 'number' ? p.MaxManualScore : 10,
                childLabel: p.ChildLabel || '',
                children: (p.Children || []).map((c, ci) => {
                    let cScore = 0
                    if (c.IsChildScore && typeof c.ChildScore === 'number') cScore = c.ChildScore
                    else if (c.IsScore && typeof c.Score === 'number') cScore = c.Score
                    return {
                        id: c.ChildId || `cid-${Date.now()}-${ci}`,
                        ChildId: c.ChildId,
                        ParentChildId: c.ParentChildId,
                        name: c.ChildName,
                        score: hasManual ? 0 : cScore,
                        isSelected: !!c.IsSelected,
                        IsSelected: !!c.IsSelected,
                    }
                }),
                QuestionId: p.QuestionId,
                IsSelected: !!p.IsSelected,
            }
        })
        setParents(newParents)
        const clbls = {}
        newParents.forEach(p => { clbls[p.id] = p.childLabel })
        setClblInputs(clbls)
    }, [existingData])

    /* ── parent CRUD ── */
    const addParent = () => {
        const nm = npName.trim()
        if (!nm) { showError('npname'); return }
        setParents(prev => [
            ...prev,
            {
                id: `pid-${Date.now()}`,
                name: nm,
                score: npScore,
                allowOther: npOther,
                allowManual: npManual,
                isMultipleAllow: npMultiple,
                maxScore: npManual ? npMaxScore : 0,
                childLabel: '',
                children: [],
            },
        ])
        setNpName('')
        setNpScore(0)
        setNpOther(false)
        setNpManual(false)
        setNpMultiple(false)
        setNpMaxScore(10)
    }

    const deleteParent = id => {
        setParents(prev => prev.filter(p => p.id !== id))
        setExp(prev => { const n = new Set(prev); n.delete(id); return n })
    }

    /* ── child CRUD ── */
    const addChild = pid => {
        const d = ncData[pid] || {}
        const nm = (d.name || '').trim()
        if (!nm) { showError(`nck${pid}`); return }
        const sc = d.score || 0
        setParents(prev => prev.map(p =>
            p.id !== pid ? p : { ...p, children: [...p.children, { id: `cid-${Date.now()}`, name: nm, score: sc, isSelected: false }] },
        ))
        setNcData(prev => ({ ...prev, [pid]: { name: '', score: 0 } }))
        setExp(prev => new Set(prev).add(pid))
    }

    const deleteChild = (pid, cid) => {
        setParents(prev => prev.map(p =>
            p.id !== pid ? p : { ...p, children: p.children.filter(c => c.id !== cid) },
        ))
    }

    const toggleExp = pid => {
        setExp(prev => {
            const n = new Set(prev)
            if (n.has(pid)) n.delete(pid)
            else n.add(pid)
            return n
        })
    }

    /* ── edit overlay ── */
    const openEditParent = p => {
        setEditCtx({ t: 'p', pid: p.id })
        setOName(p.name)
        setOScore(p.score)
        setOOther(p.allowOther)
        setOManual(p.allowManual)
        setOMultiple(!!p.isMultipleAllow)
        setOMaxScore(p.maxScore || 10)
    }

    const openEditChild = (p, c) => {
        setEditCtx({ t: 'c', pid: p.id, cid: c.id })
        setOName(c.name); setOScore(c.score)
    }

    const saveEdit = () => {
        const nm = oName.trim()
        if (!nm) { showError('oname'); return }
        if (editCtx.t === 'p') {
            setParents(prev => prev.map(p => {
                if (p.id !== editCtx.pid) return p
                return {
                    ...p,
                    name: nm,
                    score: oScore,
                    allowOther: oOther,
                    allowManual: oManual,
                    isMultipleAllow: oMultiple,
                    maxScore: oManual ? oMaxScore : 0,
                }
            }))
        } else {
            setParents(prev => prev.map(p => {
                if (p.id !== editCtx.pid) return p
                return {
                    ...p,
                    children: p.children.map(c => {
                        if (c.id !== editCtx.cid) return c
                        return { ...c, name: nm, score: oScore }
                    }),
                }
            }))
        }
        setEditCtx(null)
    }

    /* ── submit ── */
    const handleSubmit = () => {
        const parentChildRows = parents.map((p, i) => {
            const pH = p.score > 0 && !p.allowManual
            const childLabel = clblInputs[p.id] !== undefined ? clblInputs[p.id] : p.childLabel
            return {
                ParentId: p.ParentId || i + 1,
                ParentName: p.name,
                ChildLabel: childLabel,
                IsOtherOptionAvailable: p.allowOther,
                AllowManualScore: p.allowManual,
                IsMultipleAllow: !!p.isMultipleAllow,
                MaxManualScore: p.maxScore,
                IsScore: pH,
                Score: pH ? p.score : 0,
                QuestionId: p.QuestionId || question?.Id,
                IsSelected: !!p.IsSelected,
                Children: p.children.map((c, ci) => {
                    const cH = c.score > 0 && !p.allowManual
                    return {
                        ChildId: c.ChildId || ci + 1,
                        ParentId: p.ParentId || i + 1,
                        ParentChildId: c.ParentChildId || `${i + 1}-${ci + 1}`,
                        ChildName: c.name,
                        ChildLabel: childLabel,
                        IsSelected: !!c.IsSelected,
                        IsScore: cH,
                        Score: cH ? c.score : 0,
                        ChildScore: cH ? c.score : 0,
                        IsChildScore: cH,
                    }
                }),
            }
        })
        const payload = {
            ParentChildResponse: parentChildRows,
            IsOtherOptionAvailable: parentChildRows.some(p => p.IsOtherOptionAvailable),
        }
        window.parentChildModalData = payload
        onClose(payload)
    }

    /* ── stats ── */
    const totalChildren = parents.reduce((s, p) => s + p.children.length, 0)
    const scoredCount = parents.filter(p => p.score > 0).length
    const manualCount = parents.filter(p => p.allowManual).length
    const multiCount = parents.filter(p => p.isMultipleAllow).length

    const statsChips = (
        <ChipRow>
            <Chip bg="#f0f9ff" color="#0369a1" border="#bae6fd">📋 {parents.length} parent{parents.length !== 1 ? 's' : ''}</Chip>
            {totalChildren > 0 && <Chip bg="#f0fdf4" color="#16a34a" border="#bbf7d0">🔗 {totalChildren} child{totalChildren !== 1 ? 'ren' : ''}</Chip>}
            {scoredCount > 0 && <Chip bg="#fffbeb" color="#d97706" border="#fde68a">⭐ {scoredCount} scored</Chip>}
            {manualCount > 0 && <Chip bg="#f5f3ff" color="#7c3aed" border="#ddd6fe">✍ {manualCount} manual</Chip>}
            {multiCount > 0 && <Chip bg="#ecfeff" color="#0891b2" border="#a5f3fc">☑ {multiCount} multi</Chip>}
        </ChipRow>
    )

    /* ── render ── */
    return (
        <Modal open={open} onClose={() => onClose(null)} size="large">
            {/* Edit overlay (rendered inside modal so z-index stacks above modal) */}
            {editCtx && (
                <EditOverlay onClick={e => { if (e.target === e.currentTarget) setEditCtx(null) }}>
                    <EditCard>
                        {/* overlay header */}
                        <FlexRowBetween style={{ padding: '14px 18px', borderBottom: '1px solid #e5e7eb', alignItems: 'center' }}>
                            <Header as="h4" style={{ margin: 0 }}>
                                Edit {editCtx.t === 'p' ? 'Parent' : 'Child'}
                            </Header>
                            <AppButton type="button" onClick={() => setEditCtx(null)} style={{ padding: '4px 8px' }}>
                                <X size={16} weight="bold" />
                            </AppButton>
                        </FlexRowBetween>

                        {/* overlay body */}
                        <div style={{ padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: '12px', maxHeight: '70vh', overflowY: 'auto' }}>
                            <Form>
                                <Form.Field error={errIds.has('oname')}>
                                    <label>Name *</label>
                                    <Form.Input
                                        value={oName}
                                        onChange={e => setOName(e.target.value)}
                                        onKeyDown={e => e.key === 'Enter' && saveEdit()}
                                    />
                                </Form.Field>
                                <Form.Field>
                                    <label>Score (optional)</label>
                                    <NumericStepper min={0} step={1} value={oScore} onChange={v => setOScore(v)} />
                                </Form.Field>
                                {editCtx.t === 'p' && (
                                    <>
                                        <Form.Group widths="equal">
                                            <Form.Checkbox
                                                label="Allow Other Response"
                                                checked={oOther}
                                                onChange={(_, { checked }) => setOOther(checked)}
                                            />
                                            <Form.Checkbox
                                                label="Allow Manual Score"
                                                checked={oManual}
                                                onChange={(_, { checked }) => setOManual(checked)}
                                            />
                                            <Form.Checkbox
                                                label="Allow Multiple Selection"
                                                checked={oMultiple}
                                                onChange={(_, { checked }) => setOMultiple(checked)}
                                            />
                                        </Form.Group>
                                        {oManual && (
                                            <Form.Field>
                                                <label>Max Manual Score</label>
                                                <NumericStepper min={1} step={1} value={oMaxScore} onChange={v => setOMaxScore(v)} />
                                            </Form.Field>
                                        )}
                                    </>
                                )}
                            </Form>
                        </div>

                        {/* overlay footer */}
                        <FlexRow
                            className="justify-content-end"
                            style={{ padding: '12px 18px', borderTop: '1px solid #e5e7eb', gap: '8px', background: '#f9fafb' }}
                        >
                            <AppButton type="button" onClick={() => setEditCtx(null)}>Cancel</AppButton>
                            <AppPrimaryButton type="button" onClick={saveEdit}>Save Changes</AppPrimaryButton>
                        </FlexRow>
                    </EditCard>
                </EditOverlay>
            )}

            {/* Modal header */}
            <Modal.Header>
                <FlexRowBetween style={{ alignItems: 'center' }}>
                    <div>
                        <div style={{ fontWeight: 700, fontSize: '1.05rem' }}>Answer Option Builder</div>
                        <div style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: 2 }}>eAuditor — Parent‑Child Configuration</div>
                        {question && (
                            <div style={{ marginTop: 6, fontSize: '0.8rem', color: '#2563eb', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 6, padding: '3px 10px', display: 'inline-block', fontFamily: 'monospace' }}>
                                {stripHtmlTags(question)}
                            </div>
                        )}
                    </div>
                    <AppButton type="button" onClick={() => onClose(null)} style={{ padding: '4px 8px' }}>
                        <X size={18} weight="bold" />
                    </AppButton>
                </FlexRowBetween>
            </Modal.Header>

            {/* Modal content */}
            <Modal.Content style={{ padding: 0, maxHeight: '78vh', overflowY: 'hidden' }}>
                <ModalBody>

                    {/* Step 1 — Add Parent */}
                    <SectionCard>
                        <SectionCardHeader>
                            <SectionCardTitle><StepDot>1</StepDot> Add Parent Answer</SectionCardTitle>
                        </SectionCardHeader>
                        <SectionCardBody>
                            <Form>
                                <DesktopMode>
                                    <Form.Group style={{ alignItems: 'flex-end' }}>
                                        <Form.Field width={8} error={errIds.has('npname')}>
                                            <label>Answer Label *</label>
                                            <Form.Input
                                                placeholder="e.g. Yes, No, Pass, Fail…"
                                                value={npName}
                                                onChange={e => setNpName(e.target.value)}
                                                onKeyDown={e => e.key === 'Enter' && addParent()}
                                            />
                                        </Form.Field>
                                        <Form.Field width={4}>
                                            <label>Score (opt.)</label>
                                            <NumericStepper min={0} step={1} value={npScore} onChange={v => setNpScore(v)} />
                                        </Form.Field>
                                        <Form.Field width={4}>
                                            <AppPrimaryButton className="d-flex align-items-center gap-2" type="button" onClick={addParent} disabled={!npName.trim()} fluid>
                                                <Plus size={14} weight="bold" /> Add Parent
                                            </AppPrimaryButton>
                                        </Form.Field>
                                    </Form.Group>
                                </DesktopMode>
                                <MobileMode>
                                    <Form.Field error={errIds.has('npname')}>
                                        <label>Answer Label *</label>
                                        <Form.Input
                                            placeholder="e.g. Yes, No, Pass, Fail…"
                                            value={npName}
                                            onChange={e => setNpName(e.target.value)}
                                        />
                                    </Form.Field>
                                    <FlexRow style={{ gap: '8px', marginTop: '8px' }}>
                                        <NumericStepper min={0} step={1} value={npScore} onChange={v => setNpScore(v)} />
                                        <AppPrimaryButton type="button" onClick={addParent} disabled={!npName.trim()}>
                                            <Plus size={14} weight="bold" /> Add Parent
                                        </AppPrimaryButton>
                                    </FlexRow>
                                </MobileMode>

                                {/* Flags */}
                                <Form.Group widths="equal" style={{ marginTop: '10px' }}>
                                    <Form.Checkbox
                                        label="Allow Other Response"
                                        checked={npOther}
                                        onChange={(_, { checked }) => setNpOther(checked)}
                                    />
                                    <Form.Checkbox
                                        label="Allow Manual Score"
                                        checked={npManual}
                                        onChange={(_, { checked }) => setNpManual(checked)}
                                    />
                                    <Form.Checkbox
                                        label="Allow Multiple Selection"
                                        checked={npMultiple}
                                        onChange={(_, { checked }) => setNpMultiple(checked)}
                                    />
                                </Form.Group>
                                {npManual && (
                                    <Form.Field style={{ marginTop: '8px' }}>
                                        <label>Max Manual Score</label>
                                        <NumericStepper min={1} step={1} value={npMaxScore} onChange={v => setNpMaxScore(v)} />
                                        <div style={{ fontSize: '11px', color: '#7c3aed', marginTop: 4 }}>Auditor can enter up to this value during inspection</div>
                                    </Form.Field>
                                )}
                            </Form>
                        </SectionCardBody>
                    </SectionCard>

                    {/* Step 2 — Configured Answers */}
                    <SectionCard style={{ flex: 1, minHeight: 0 }}>
                        <SectionCardHeader>
                            <SectionCardTitle><StepDot>2</StepDot> Configured Answers</SectionCardTitle>
                            {statsChips}
                        </SectionCardHeader>
                        <SectionCardBody>
                            {parents.length === 0 ? (
                                <EmptyState>
                                    <ListDashes size={32} color="#9ca3af" style={{ marginBottom: 8, display: 'block', margin: '0 auto 8px' }} />
                                    <div style={{ fontSize: '13px', fontWeight: 500, color: '#6b7280' }}>No answers added yet</div>
                                    <div style={{ fontSize: '12px', marginTop: 4 }}>Use Step 1 above to add your first parent answer</div>
                                </EmptyState>
                            ) : (
                                parents.map(p => {
                                    const isExp = exp.has(p.id)
                                    const hasChildren = p.children.length > 0
                                    const cVal = clblInputs[p.id] !== undefined ? clblInputs[p.id] : p.childLabel
                                    const dName = ncData[p.id]?.name || ''
                                    const dScore = ncData[p.id]?.score || 0

                                    return (
                                        <ParentCard key={p.id} hasChildren={hasChildren} allowManual={p.allowManual}>
                                            <ParentRow onClick={() => toggleExp(p.id)}>
                                                <ChevBtn
                                                    type="button"
                                                    open={isExp}
                                                    onClick={e => { e.stopPropagation(); toggleExp(p.id) }}
                                                >
                                                    {isExp ? <CaretDown size={10} weight="bold" /> : <CaretRight size={10} weight="bold" />}
                                                </ChevBtn>
                                                <div style={{ flex: 1, fontSize: '13.5px', fontWeight: 600, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</div>
                                                <BadgeRow>
                                                    {p.score > 0 && !p.allowManual && <Badge bg="#fffbeb" color="#d97706" border="#fde68a">⭐ {p.score} pts</Badge>}
                                                    {p.allowManual && p.maxScore > 0 && <Badge bg="#f5f3ff" color="#7c3aed" border="#ddd6fe">max {p.maxScore}</Badge>}
                                                    {p.allowOther && <Badge bg="#f0fdf4" color="#16a34a" border="#bbf7d0">Other</Badge>}
                                                    {p.allowManual && <Badge bg="#f5f3ff" color="#7c3aed" border="#ddd6fe">Manual</Badge>}
                                                    {p.isMultipleAllow && <Badge bg="#ecfeff" color="#0891b2" border="#a5f3fc">Multi</Badge>}
                                                    {hasChildren && <Badge bg="#eff6ff" color="#2563eb" border="#bfdbfe">{p.children.length} child{p.children.length !== 1 ? 'ren' : ''}</Badge>}
                                                </BadgeRow>
                                                <FlexRow
                                                    style={{ gap: '4px' }}
                                                    onClick={e => e.stopPropagation()}
                                                >
                                                    <AppButton
                                                        type="button"
                                                        title="Add Child"
                                                        style={{ padding: '3px 8px' }}
                                                        onClick={() => { if (!isExp) toggleExp(p.id) }}
                                                    >
                                                        <Plus size={13} weight="bold" />
                                                    </AppButton>
                                                    <AppEditButton onClick={() => openEditParent(p)} />
                                                    <AppRemoveButtonPopup onConfirm={() => deleteParent(p.id)} />
                                                </FlexRow>
                                            </ParentRow>

                                            <ChildrenPanel open={isExp}>
                                                {/* Child label */}
                                                <ChildLabelRow>
                                                    <Tag size={13} color="#9ca3af" />
                                                    <ChildLabelText>Child Label:</ChildLabelText>
                                                    <ChildLabelInput
                                                        type="text"
                                                        placeholder="e.g. Select reason…"
                                                        value={cVal}
                                                        onChange={e => setClblInputs({ ...clblInputs, [p.id]: e.target.value })}
                                                        onKeyDown={e => {
                                                            if (e.key === 'Enter') {
                                                                setParents(prev => prev.map(px => px.id === p.id ? { ...px, childLabel: cVal } : px))
                                                            }
                                                        }}
                                                    />
                                                    <AppButton
                                                        type="button"
                                                        style={{ padding: '2px 8px', fontSize: '11px' }}
                                                        onClick={() => setParents(prev => prev.map(px => px.id === p.id ? { ...px, childLabel: cVal } : px))}
                                                    >
                                                        Set
                                                    </AppButton>
                                                </ChildLabelRow>

                                                {/* Child list */}
                                                {p.children.map(c => (
                                                    <ChildRow key={c.id}>
                                                        <div style={{ width: 5, height: 5, borderRadius: '50%', background: '#2563eb', flexShrink: 0 }} />
                                                        <ChildLabel>{c.name}</ChildLabel>
                                                        {c.score > 0 && !p.allowManual && <ScorePill>{c.score} pts</ScorePill>}
                                                        <FlexRow style={{ gap: '4px' }}>
                                                            <AppEditButton onClick={() => openEditChild(p, c)} />
                                                            <AppRemoveButtonPopup onConfirm={() => deleteChild(p.id, c.id)} />
                                                        </FlexRow>
                                                    </ChildRow>
                                                ))}

                                                {/* Add child row */}
                                                <AddChildRow>
                                                    <Plus size={12} color="#2563eb" weight="bold" />
                                                    <input
                                                        type="text"
                                                        placeholder="Child name…"
                                                        value={dName}
                                                        style={{
                                                            flex: 1,
                                                            fontSize: '12px',
                                                            padding: '5px 9px',
                                                            borderRadius: '6px',
                                                            border: errIds.has(`nck${p.id}`) ? '1.5px solid #dc2626' : '1.5px solid #d1d5db',
                                                            height: '30px',
                                                            minWidth: '120px',
                                                            outline: 'none',
                                                        }}
                                                        onChange={e => setNcData({ ...ncData, [p.id]: { ...ncData[p.id], name: e.target.value } })}
                                                        onKeyDown={e => e.key === 'Enter' && addChild(p.id)}
                                                    />
                                                    <NumericStepper
                                                        min={0}
                                                        step={1}
                                                        value={dScore}
                                                        onChange={v => setNcData({ ...ncData, [p.id]: { ...ncData[p.id], score: v } })}
                                                    />
                                                    <AppPrimaryButton type="button" onClick={() => addChild(p.id)}>
                                                        Add
                                                    </AppPrimaryButton>
                                                </AddChildRow>
                                            </ChildrenPanel>
                                        </ParentCard>
                                    )
                                })
                            )}
                        </SectionCardBody>
                    </SectionCard>
                </ModalBody>
            </Modal.Content>

            <Modal.Actions style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
                {statsChips}
                <FlexRow style={{ gap: '8px' }}>
                    <AppButton type="button" onClick={() => onClose(null)}>Cancel</AppButton>
                    <AppPrimaryButton className="d-flex align-items-center gap-2" type="button" onClick={handleSubmit}>
                        <Check size={15} weight="bold" /> Save Configuration
                    </AppPrimaryButton>
                </FlexRow>
            </Modal.Actions>
        </Modal>
    )
}

// export const ResponseTypeSelectionModal = ({
//     responseTypes,
//     onClose,
//     setCreate,
//     setEditMultipleResponses,
// }) => {
//     console.log('asdsdhjsadhjkas', responseTypes?.SingleResponses)
//     const isMobile = useTabletAndBelowMediaQuery()
//     return (
//         <ResponseModal>
//             <FlexRowBetween className="my-3 align-items-center d-flex flex-row">
//                 <Header className="mb-0 ">Multiple Choice Responses</Header>
//                 <AppPrimaryButton onClick={() => setCreate(true)}>
//                     <MobileMode>
//                         <Plus />
//                     </MobileMode>
//                     <DesktopMode>Create New</DesktopMode>
//                 </AppPrimaryButton>
//             </FlexRowBetween>
//             <MultiChoiceWrapper isMobile={isMobile}>
//                 {(responseTypes?.GroupedResponse || []).map(item => (
//                     <MultiChoiceContainer
//                         key={item.GroupId}
//                         className="single-option"
//                         onClick={() =>
//                             onClose({
//                                 item,
//                                 type: TemplateResponseType.Multiple,
//                             })
//                         }
//                     >
//                         <ResponseTag
//                             item={item}
//                             type={TemplateResponseType.Multiple}
//                         />
//                         <PencilSimple
//                             size={28}
//                             onClick={() => {
//                                 setEditMultipleResponses(item)
//                             }}
//                         />
//                     </MultiChoiceContainer>
//                 ))}
//             </MultiChoiceWrapper>
//             <div className="mt-4  font-bold">
//                 <PageHeader header="Other Responses" />
//                 <MultiChoiceWrapper>
//                     {responseTypes?.SingleResponses.map(item => (
//                         <div
//                             key={item.Id}
//                             className="single-option"
//                             onClick={() =>
//                                 onClose({
//                                     item,
//                                     type: TemplateResponseType.Single,
//                                 })
//                             }
//                         >
//                             <ResponseTag
//                                 item={item}
//                                 type={TemplateResponseType.Single}
//                             />
//                         </div>
//                     ))}
//                 </MultiChoiceWrapper>
//             </div>
//         </ResponseModal>
//     )
// }

export const ResponseTypeSelectionModal = ({
    responseTypes,
    onClose,
    setCreate,
    setEditMultipleResponses,
    question,
    onParentChildDataReceived,
    onAutoNumberDataReceived,
    existingData,
    existingAutoNumberData = null,
}) => {
    const isMobile = useTabletAndBelowMediaQuery()
    const { user } = useAuth()
    const formulaEnabled = isFormulaQuestionCreationEnabled(
        user?.CompanyId || user?.Companies?.[0]?.CompanyId,
        user?.Capabilities,
    )

    return (
        <ResponseModal>
            <FlexRowBetween className="my-3 align-items-center d-flex flex-row">
                <Header className="mb-0">Multiple Choice Responses</Header>
                <AppPrimaryButton onClick={() => setCreate(true)}>
                    <MobileMode><Plus /></MobileMode>
                    <DesktopMode>Create New</DesktopMode>
                </AppPrimaryButton>
            </FlexRowBetween>
            <MultiChoiceWrapper isMobile={isMobile}>
                {(responseTypes?.GroupedResponse || []).map(item => (
                    <MultiChoiceContainer
                        key={item.GroupId}
                        className="single-option"
                        onClick={() => onClose({ item, type: TemplateResponseType.Multiple })}
                    >
                        <ResponseTag
                            item={item}
                            type={TemplateResponseType.Multiple}
                            question={question}
                            existingData={responseTypes?.SingleResponses}
                        />
                        {item.Items && item.Items.some(opt => opt.IsEditAvailable) && (
                            <PencilSimple
                                size={28}
                                onClick={e => {
                                    e.stopPropagation()
                                    setEditMultipleResponses(item)
                                }}
                            />
                        )}
                    </MultiChoiceContainer>
                ))}
            </MultiChoiceWrapper>
            <div className="mt-4 font-bold">
                <PageHeader header="Other Responses" />
                <MultiChoiceWrapper>
                    {(responseTypes?.SingleResponses || [])
                        .filter(item => {
                            if (
                                item?.DisplayText?.trim()?.toLowerCase() ===
                                'barcode'
                            ) {
                                return false
                            }
                            if (!formulaEnabled && isFormulaResponseItem(item)) {
                                return false
                            }
                            return true
                        })
                        .map(item => {
                            const isCreateMultiResponseOption =
                                Number(item?.Id) === 12
                            const displayItem = isCreateMultiResponseOption
                                ? {
                                    ...item,
                                    IconDisplayText: item.DisplayText,
                                    DisplayText: `${item.DisplayText} (Create New)`,
                                }
                                : item

                            return (
                                <div
                                    key={item.Id}
                                    className="single-option"
                                    onClick={() => {
                                        if (isCreateMultiResponseOption) {
                                            onClose()
                                            setCreate(true)
                                            return
                                        }
                                        onClose({
                                            item,
                                            type: TemplateResponseType.Single,
                                        })
                                    }}
                                >
                                    <ResponseTag
                                        item={displayItem}
                                        type={TemplateResponseType.Single}
                                        question={question}
                                        onParentChildDataReceived={
                                            onParentChildDataReceived
                                        }
                                        onAutoNumberDataReceived={
                                            onAutoNumberDataReceived
                                        }
                                        existingData={existingData}
                                        existingAutoNumberData={
                                            existingAutoNumberData
                                        }
                                    />
                                </div>
                            )
                        })}
                </MultiChoiceWrapper>
            </div>
        </ResponseModal>
    )
}
